import React, { useState } from 'react';
import { 
  FileText, 
  ShieldCheck, 
  Download, 
  Copy, 
  CheckCircle2, 
  Building2, 
  Send,
  Info
} from 'lucide-react';

export const CreditBanGeneratorPage: React.FC = () => {
  const [fullName, setFullName] = useState('Alex Mitchell');
  const [dob, setDob] = useState('1988-04-12');
  const [currentAddress, setCurrentAddress] = useState('Unit 4, 120 Campbell St, Surry Hills NSW 2010');
  const [licenceNumber, setLicenceNumber] = useState('98124019');
  const [policeReportNumber, setPoliceReportNumber] = useState('E10928819');
  const [copied, setCopied] = useState(false);

  const declarationText = `FORMAL NOTICE OF STATUTORY CREDIT BAN REQUEST
UNDER SECTION 20HS OF THE PRIVACY ACT 1988 (CTH)

To: 
Equifax Australia (banrequest@equifax.com.au)
Experian Australia (creditbans@experian.com)
illion Australia (creditban@illion.com.au)

DATE: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}

I, ${fullName} (DOB: ${dob}), hereby request an immediate statutory Credit Ban to be placed on my consumer credit files held across all Australian credit reporting bodies.

INDIVIDUAL IDENTIFIERS:
• Full Legal Name: ${fullName}
• Date of Birth: ${dob}
• Current Address: ${currentAddress}
• Australian Driver Licence: ${licenceNumber}
• Police Incident / IDCARE Case Reference: ${policeReportNumber}

LEGAL BASIS:
Pursuant to Section 20HS of the Privacy Act 1988, I believe on reasonable grounds that I have been, or am likely to be, a victim of fraud. 

Under Subsection 20HS(2), no credit reporting body may use or disclose my credit reporting information without my express written authorization for the duration of this statutory ban.

Furthermore, pursuant to Section 20HS(5), I request the receiving credit bureau immediately notify the other registered credit reporting bodies of this ban request.

Signed: ${fullName}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(declarationText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([declarationText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Statutory_Credit_Ban_Declaration_${fullName.replace(/\s+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <FileText className="w-3.5 h-3.5" />
          <span>PRIVACY ACT 1988 SECTION 20HS STATUTORY DISPATCHER</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white">Statutory Credit Ban Declaration Generator</h1>
        <p className="text-xs text-slate-400 max-w-xl mx-auto">
          Generate an official, legally binding credit suppression notice for Equifax, Experian, and illion to stop unauthorized lending inquiries.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 text-xs shadow-xl">
          <h3 className="font-bold text-white text-sm">Fill Your Verification Details:</h3>

          <div className="space-y-3">
            <div>
              <label className="block text-slate-300 mb-1">Full Legal Name:</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-slate-300 mb-1">Date of Birth:</label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono"
              />
            </div>

            <div>
              <label className="block text-slate-300 mb-1">Current Residential Address:</label>
              <input
                type="text"
                value={currentAddress}
                onChange={(e) => setCurrentAddress(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-slate-300 mb-1">Driver Licence Number:</label>
              <input
                type="text"
                value={licenceNumber}
                onChange={(e) => setLicenceNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono"
              />
            </div>

            <div>
              <label className="block text-slate-300 mb-1">Police / ReportCyber Event #:</label>
              <input
                type="text"
                value={policeReportNumber}
                onChange={(e) => setPoliceReportNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2 text-xs">
              <span className="font-bold text-white uppercase">Generated Statutory Declaration</span>
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg flex items-center gap-1 font-semibold"
                >
                  <Copy className="w-3 h-3" />
                  <span>{copied ? 'Copied!' : 'Copy Text'}</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-1 font-semibold"
                >
                  <Download className="w-3 h-3" />
                  <span>Download .txt</span>
                </button>
              </div>
            </div>

            <textarea
              readOnly
              rows={14}
              value={declarationText}
              className="w-full p-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 font-mono text-[11px] leading-relaxed resize-none focus:outline-none"
            />
          </div>

          <div className="p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-800 text-emerald-200 text-xs flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span>Email this declaration to Equifax Australia (banrequest@equifax.com.au) to initiate a free 21-day statutory lock.</span>
          </div>
        </div>
      </div>
    </div>
  );
};
